import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { FloatingPortal, offset, size, useClick, useDismiss, useFloating, useInteractions } from '@floating-ui/react'
import { useQuery, useMutation } from 'urql'
import { graphql } from 'app/generated/gql'
import { CustomQuestionType, SchedulingConfigurationJson } from 'app/types/entities'
import { toSchedulingConfigurationInput, toSchedulingConfigurationJson } from 'app/lib/schedulingConfiguration'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'

// This page also edits the lead override, so it selects those fields alongside the questions.
const SCHEDULING_SETTINGS_QUERY = graphql(`
  query SchedulingSettings($locationId: ID!) {
    schedulingConfiguration(locationId: $locationId) {
      id
      locationId
      allowDoubleBooking
      insurancesAccepted
      insurancesRejected
      isStateInsuranceAccepted
      granularity
      customQuestions {
        id
        fieldName
        required
        fieldType
        enabled
        description
        marketingConsent
        optIn
      }
      leadOverrideEnabled
      leadOverrideConfig {
        amount
        unit
      }
    }
  }
`)

const UPDATE_SCHEDULING_CONFIGURATION_MUTATION = graphql(`
  mutation UpdateSchedulingConfiguration($locationId: ID!, $input: UpdateSchedulingConfigurationInput!) {
    updateSchedulingConfiguration(locationId: $locationId, input: $input) {
      id
      locationId
      allowDoubleBooking
      insurancesAccepted
      insurancesRejected
      isStateInsuranceAccepted
      granularity
      customQuestions {
        id
        fieldName
        required
        fieldType
        enabled
        description
        marketingConsent
        optIn
      }
      leadOverrideEnabled
      leadOverrideConfig {
        amount
        unit
      }
    }
  }
`)

export type UseSchedulingSettingsReturn = {
  schedulingConfiguration: SchedulingConfigurationJson | undefined
  setSchedulingConfiguration: React.Dispatch<React.SetStateAction<SchedulingConfigurationJson | undefined>>
  customQuestions: CustomQuestionType[]
  setCustomQuestions: React.Dispatch<React.SetStateAction<CustomQuestionType[]>>
  showQuestionPanel: boolean
  setShowQuestionPanel: React.Dispatch<React.SetStateAction<boolean>>
  showLeadOverride: boolean
  setShowLeadOverride: React.Dispatch<React.SetStateAction<boolean>>
  isDirtyForm: boolean
  setIsDirtyForm: React.Dispatch<React.SetStateAction<boolean>>
  locationId: string | undefined
  saveRequest: { inFlight: boolean }
  floatingData: ReturnType<typeof useFloating>
  getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps']
  getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps']
  floatingDataLeadOverride: ReturnType<typeof useFloating>
  getLeadOverrideReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps']
  getLeadOverrideFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps']
  handleSaveConfiguration: () => Promise<void>
  onMoveQuestion: (dragIndex: number, hoverIndex: number) => void
  onDropQuestion: () => Promise<void>
  onQuestionCreateCompleted: (question: CustomQuestionType) => Promise<void>
  onQuestionUpdateCompleted: (question: CustomQuestionType) => void
  onQuestionDeleted: (id: string) => void
  onLeadOverrideSaved: (config: any) => void
  onLeadOverrideToggle: (enabled: boolean) => Promise<void>
}

export function useSchedulingSettings(): UseSchedulingSettingsReturn {
  const { locationId } = useParams()
  const [schedulingConfiguration, setSchedulingConfiguration] = useState<SchedulingConfigurationJson | undefined>()
  const [customQuestions, setCustomQuestions] = useState<CustomQuestionType[]>([])
  const [showQuestionPanel, setShowQuestionPanel] = useState(false)
  const [showLeadOverride, setShowLeadOverride] = useState(false)
  const [isDirtyForm, setIsDirtyForm] = useState<boolean>(false)
  const [saveInFlight, setSaveInFlight] = useState(false)
  const saveRequest = { inFlight: saveInFlight }
  const [placement, setPlacement] = useState<`bottom-start` | `top-start`>(`bottom-start`)

  const PANEL_MAX_HEIGHT = 584

  const floatingData = useFloating({
    placement,
    middleware: [
      offset(8),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${PANEL_MAX_HEIGHT}px`,
            width: `${rects.reference.width}px`,
            minWidth: `450px`,
            overflowY: `auto`,
          })
        },
        padding: 10,
      }),
    ],
    open: showQuestionPanel,
    onOpenChange: setShowQuestionPanel,
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floatingData.context),
    useDismiss(floatingData.context),
  ])

  const floatingDataLeadOverride = useFloating({
    placement: `bottom-start`,
    middleware: [
      offset(8),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            width: `${rects.reference.width}px`,
            minWidth: `450px`,
          })
        },
        padding: 10,
      }),
    ],
    open: showLeadOverride,
    onOpenChange: setShowLeadOverride,
  })
  const { getReferenceProps: getLeadOverrideReferenceProps, getFloatingProps: getLeadOverrideFloatingProps } =
    useInteractions([useClick(floatingDataLeadOverride.context), useDismiss(floatingDataLeadOverride.context)])

  useEffect(() => {
    if (showQuestionPanel) {
      const referenceElement = floatingData.refs.reference.current
      if (referenceElement) {
        const rect = (referenceElement as Element).getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - rect.bottom - 10
        const spaceAbove = rect.top - 10
        if (spaceBelow >= PANEL_MAX_HEIGHT) {
          setPlacement(`bottom-start`)
        } else if (spaceAbove >= PANEL_MAX_HEIGHT) {
          setPlacement(`top-start`)
        } else {
          setPlacement(spaceBelow > spaceAbove ? `bottom-start` : `top-start`)
        }
      }
    }
  }, [showQuestionPanel, floatingData.refs.reference])

  const [settingsResult] = useQuery({
    query: SCHEDULING_SETTINGS_QUERY,
    variables: { locationId: locationId! },
    pause: !locationId,
  })

  useEffect(() => {
    if (settingsResult.data?.schedulingConfiguration) {
      const sc = toSchedulingConfigurationJson(settingsResult.data.schedulingConfiguration)
      setSchedulingConfiguration(sc)
      setCustomQuestions(sc.customQuestions ?? [])
    }
  }, [settingsResult.data])

  const [, executeSave] = useMutation(UPDATE_SCHEDULING_CONFIGURATION_MUTATION)

  const handleSaveConfiguration = async () => {
    setSaveInFlight(true)
    const result = await executeSave({
      locationId: locationId!,
      input: toSchedulingConfigurationInput(schedulingConfiguration, customQuestions),
    })
    setSaveInFlight(false)

    if (result.error) {
      notifyError(`Failed to update configuration`)
    } else if (result.data?.updateSchedulingConfiguration) {
      const sc = toSchedulingConfigurationJson(result.data.updateSchedulingConfiguration)
      setSchedulingConfiguration(sc)
      setCustomQuestions(sc.customQuestions ?? [])
      notifySuccess(`Configuration saved successfully`)
      setIsDirtyForm(false)
    }
  }

  const onMoveQuestion = (dragIndex: number, hoverIndex: number) => {
    const dragQuestion = customQuestions[dragIndex]
    setCustomQuestions((prev) => {
      const updatedQuestions = [...prev]
      updatedQuestions.splice(dragIndex, 1)
      updatedQuestions.splice(hoverIndex, 0, dragQuestion)
      return updatedQuestions
    })
    setIsDirtyForm(true)
  }

  const onDropQuestion = async () => {
    setIsDirtyForm(true)
  }

  const onQuestionCreateCompleted = async (question: CustomQuestionType) => {
    setShowQuestionPanel(false)
    setCustomQuestions((prev) => [...prev, question])
    setIsDirtyForm(true)
  }

  const onQuestionUpdateCompleted = (question: CustomQuestionType) => {
    const index = customQuestions.findIndex((q) => q.id === question.id)
    if (index !== -1) {
      setCustomQuestions((prev) => {
        const updatedQuestions = [...prev]
        updatedQuestions[index] = question
        return updatedQuestions
      })
      setIsDirtyForm(true)
    }
  }

  const onQuestionDeleted = (id: string) => {
    setCustomQuestions((prev) => prev.filter((q) => q.id !== id))
    setIsDirtyForm(true)
  }

  const onLeadOverrideSaved = (config: any) => {
    setShowLeadOverride(false)
    setSchedulingConfiguration({
      ...schedulingConfiguration,
      leadOverrideConfig: config,
    })
    setIsDirtyForm(true)
  }

  const onLeadOverrideToggle = async (enabled: boolean) => {
    setSchedulingConfiguration({
      ...schedulingConfiguration,
      leadOverrideEnabled: enabled,
    })
    setIsDirtyForm(true)
  }

  return {
    schedulingConfiguration,
    setSchedulingConfiguration,
    customQuestions,
    setCustomQuestions,
    showQuestionPanel,
    setShowQuestionPanel,
    showLeadOverride,
    setShowLeadOverride,
    isDirtyForm,
    setIsDirtyForm,
    locationId,
    saveRequest,
    floatingData,
    getReferenceProps,
    getFloatingProps,
    floatingDataLeadOverride,
    getLeadOverrideReferenceProps,
    getLeadOverrideFloatingProps,
    handleSaveConfiguration,
    onMoveQuestion,
    onDropQuestion,
    onQuestionCreateCompleted,
    onQuestionUpdateCompleted,
    onQuestionDeleted,
    onLeadOverrideSaved,
    onLeadOverrideToggle,
  }
}
